import { Form, Modal, Select, Space } from 'antd';
import { DefaultOptionType } from 'antd/lib/select';
import dayjs, { Dayjs } from 'dayjs';
import _ from 'lodash';
import React, { ReactElement, useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Dispatch } from 'redux';
import IApplicationProperty from '~Api/Application/IApplicationProperty';
import IApplicationPropertyValuer from '~Api/Application/IApplicationPropertyValuer';
import PropertyValuerValuationTypeEnum from '~Api/Application/PropertyValuerValuationType';
import { applicationPropertyValuerQuoteRequestedAction } from '~Applications/actions';
import { propertyValuerValuationTypeLabels } from '~Applications/propertyValuerValuationTypeLabels';
import DatePicker from '~UI/DatePicker';
import TimePicker from '~UI/TimePicker';
import IValuer from '~Valuers/IValuer';

export default function QuoteRequestedModal(props: {
    applicationProperty: IApplicationProperty;
    isOpen: boolean;
    onCancel: () => void;
    propertyValuer: IApplicationPropertyValuer;
    valuer: IValuer;
}): ReactElement {
    const {
        applicationProperty,
        isOpen,
        onCancel,
        propertyValuer,
        valuer,
    } = props;

    const dispatch: Dispatch = useDispatch();

    const [ quoteRequestedDateTime, setQuoteRequestedDateTime ] = useState<string | null>(null);
    const [ valuationType, setValuationType ] = useState<PropertyValuerValuationTypeEnum | null>(null);

    const quoteRequestedDateTimeDayjs: Dayjs | null = quoteRequestedDateTime ? dayjs(quoteRequestedDateTime) : null;

    const onChangeDate: (date: Dayjs) => void = useCallback((date: Dayjs): void => {
        setQuoteRequestedDateTime((previousQuoteRequestedDateTime: string | null): string => dayjs(
            `${date.format('YYYY-MM-DD')} ${dayjs(previousQuoteRequestedDateTime || undefined).format('HH:mm')}`,
        ).format());
    }, []);

    const onChangeTime: (time: Dayjs) => void = useCallback((time: Dayjs): void => {
        setQuoteRequestedDateTime((previousQuoteRequestedDateTime: string | null): string => dayjs(
            `${dayjs(previousQuoteRequestedDateTime || undefined).format('YYYY-MM-DD')} ${time.format('HH:mm')}`,
        ).format());
    }, []);

    const onChangeValuationType: (value: PropertyValuerValuationTypeEnum) => void = useCallback(
        (value: PropertyValuerValuationTypeEnum): void => {
            setValuationType(value);
        },
        [],
    );

    const onClickOk: () => void = useCallback((): void => {
        dispatch(applicationPropertyValuerQuoteRequestedAction(
            applicationProperty.uuid,
            valuer.uuid,
            quoteRequestedDateTime,
            valuationType || (propertyValuer && propertyValuer.valuationType),
        ));

        onCancel();
    }, [
        applicationProperty.uuid,
        dispatch,
        onCancel,
        propertyValuer,
        quoteRequestedDateTime,
        valuationType,
        valuer.uuid,
    ]);

    const valuationTypeOptions: DefaultOptionType[] = _.map(
        propertyValuerValuationTypeLabels,
        (label: string, value: string): DefaultOptionType => ({
            label,
            value,
        }),
    );

    return (
        <Modal
            okText='Quote Requested'
            onCancel={onCancel}
            onOk={onClickOk}
            open={isOpen}
            title='Quote Requested'
            wrapClassName='property-valuation-request-quote-modal'
        >
            <Form.Item className='requested-date' label='Request Date'>
                <Space>
                    <DatePicker
                        className='requested-date-datepicker'
                        format='DD/MM/YYYY'
                        onChange={onChangeDate}
                        value={quoteRequestedDateTimeDayjs || dayjs()}
                    />
                    <TimePicker
                        className='requested-date-timepicker'
                        format='H:mm'
                        onChange={onChangeTime}
                        value={quoteRequestedDateTimeDayjs || dayjs()}
                    />
                </Space>
            </Form.Item>
            <Form.Item className='valuation-type' label='Valuation Type'>
                <Select
                    onChange={onChangeValuationType}
                    options={valuationTypeOptions}
                    value={valuationType || (propertyValuer && propertyValuer.valuationType)}
                />
            </Form.Item>
        </Modal>
    );
}
